import { Button, Snackbar, TextField, IconButton } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { type SyntheticEvent, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Alert } from "@mui/lab";
import { useFormsContext } from "@setup/configurations/forms/context/FormsContext";
import {
	ButtonWithLoading,
	ErrorModal,
	MesfModal,
	AssetTreePicker,
	useAssetContext,
	TimeService,
	getMomentTz,
} from "@dexteel/mesf-core";
import type { FormAsset } from "@setup/configurations/forms/models/FormAsset";
import { upsertFormAsset } from "@setup/configurations/forms/repositories/FormsRepository";
import { ScheduleCodePicker } from "../picker/ScheduleCodePicker";
import { INITIAL_VALUES_FORM_ASSET } from "@setup/configurations/forms/models/FormAssetInitialValue";
import { DateTimePicker } from "@mui/x-date-pickers";
import EditIcon from "@mui/icons-material/Edit";
import cronstrue from "cronstrue";

const moment = getMomentTz();

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const NewFormAssetModal = ({ show, onHide }: Props) => {
	const [addSuccess, setAddSuccess] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState<string>("");
	const {
		state: {
			FormId,
			userFormAssetData: { AssetId, AssetName },
		},
		actions: {
			setAssetId,
			setAssetName,
			setFormAssetFormAssetId,
			setFormAssetAssetId,
		},
	} = useFormsContext();

	const {
		state: { allAssets },
	} = useAssetContext();

	const [cronExpression, setCronExpression] = useState<string>("");
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isCustomSchedule, setIsCustomSchedule] = useState(false);

	const {
		control,
		handleSubmit,
		setValue,
		watch,
		formState: { isValid, errors },
		reset,
	} = useForm<FormAsset>({
		defaultValues: INITIAL_VALUES_FORM_ASSET,
	});

	const schedulePolicyCode = watch("SchedulePolicyCode");

	const handleCancel = async () => {
		reset();
		setAssetId(0);
		setAssetName("Plant");
		onHide(false);
	};

	const changeSearchAsset = (assetId: number | null) => {
		const id = assetId || 0;
		setValue("AssetId", id);
		setAssetId(id);
		setFormAssetAssetId(id);

		// Find asset name
		const foundAsset = allAssets.find((asset) => asset.AssetId === id);
		if (foundAsset) {
			setAssetName(foundAsset.AssetName);
		}
	};

	const handleClose = (
		event: Event | React.SyntheticEvent,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setAddSuccess(false);
	};

	const onSubmit = async (data: FormAsset) => {
		setIsSubmitLoading(true);

		const submitData = {
			...data,
			CronExpression: cronExpression,
		};

		const res = await upsertFormAsset(submitData);
		if (res.ok) {
			setFormAssetFormAssetId(null);
			setIsSubmitLoading(false);
			setAddSuccess(true);
			reset();
			onHide(true);
		} else {
			setIsSubmitLoading(false);
			setError(res.message);
		}
	};

	const handleScheduleStartChange = (date: Date) => {
		setValue("StartDate", date);
	};

	useEffect(() => {
		if (show) {
			reset();
			setAssetId(0);
			setAssetName("Plant");
			setValue("AssetId", 0);
			setValue("FormId", FormId);
			setValue("StartDate", new Date());
			setIsSubmitLoading(false);
			setCronExpression("");
			setIsCustomSchedule(false);
		}
	}, [show]);

	useEffect(() => {
		setIsCustomSchedule(schedulePolicyCode === "P");
	}, [schedulePolicyCode]);

	return (
		<>
			<MesfModal
				title="NEW ASSET"
				open={show}
				handleClose={() => handleCancel()}
				maxWidth="sm"
				id="new-form-asset-modal"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers>
						<Grid2 container spacing={2}>
							<Grid2 size={12}>
								<Controller
									name="AssetId"
									control={control}
									render={({ field: { value } }) => (
										<AssetTreePicker
											label="Asset"
											value={value || 0}
											onChange={changeSearchAsset}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ md: 4, xs: 12 }}>
								<Controller
									name="SchedulePolicyCode"
									control={control}
									render={({ field: { onChange, value } }) => (
										<ScheduleCodePicker
											value={value as string}
											onChange={onChange}
											cronExpression={cronExpression}
											onCronExpressionChange={setCronExpression}
											isModalOpen={isModalOpen}
											setIsModalOpen={setIsModalOpen}
											hideScheduleStart={setIsCustomSchedule}
											onScheduleStartChange={handleScheduleStartChange}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ md: 2, xs: 12 }}>
								<Controller
									name="Periods"
									control={control}
									rules={{ required: true, pattern: /^[0-9]*$/ }}
									render={({
										field: { onChange, value },
										fieldState: { error },
									}) => (
										<TextField
											margin="dense"
											label="Periods"
											variant="outlined"
											fullWidth
											autoComplete="off"
											value={value ?? ""}
											onChange={(evt) => {
												const newValue = evt.target.value.replace(
													/[^0-9]/g,
													"",
												);
												if (newValue.length <= 99) onChange(newValue);
											}}
											error={!!error}
											helperText={error ? "Enter a period number" : ""}
											slotProps={{ inputLabel: { shrink: true } }}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ md: 6, xs: 12 }}>
								<Controller
									name="StartDate"
									control={control}
									render={({ field }) => (
										<DateTimePicker
											label="Start Date"
											timezone={
												isCustomSchedule
													? "UTC"
													: TimeService.getInstance().getServerTimeZone()
											}
											format={
												isCustomSchedule ? "MM/DD/YYYY" : "MM/DD/YYYY HH:mm"
											}
											timeSteps={{ minutes: 1 }}
											value={moment(field.value) as any}
											onChange={field.onChange}
											inputRef={field.ref}
											disabled={isCustomSchedule}
											slotProps={{
												textField: {
													fullWidth: true,
													variant: "outlined",
													margin: "dense",
													error: !!errors.StartDate,
													InputLabelProps: { shrink: true },
												},
											}}
										/>
									)}
								/>
							</Grid2>
							{isCustomSchedule && (
								<Grid2 size={12}>
									<TextField
										label="Schedule"
										variant="outlined"
										fullWidth
										margin="dense"
										placeholder="Choose your schedule"
										value={
											cronExpression ? cronstrue.toString(cronExpression) : ""
										}
										slotProps={{
											inputLabel: { shrink: true },
											input: {
												readOnly: true,
												endAdornment: (
													<IconButton
														edge="end"
														onClick={() => setIsModalOpen(true)}
														size="small"
													>
														<EditIcon />
													</IconButton>
												),
											},
										}}
									/>
								</Grid2>
							)}
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 16px 16px" }}>
						<Grid2 container spacing={2} justifyContent="flex-end" width="100%">
							<Grid2 size={{ md: 3, xs: 12 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={handleCancel}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 12 }}>
								<ButtonWithLoading
									fullWidth
									isLoading={isSubmitLoading}
									disabled={
										!isValid ||
										(schedulePolicyCode === "P" &&
											(!cronExpression || cronExpression.trim().length === 0))
									}
									variant="contained"
									color="primary"
									type="submit"
								>
									Save
								</ButtonWithLoading>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<Snackbar
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				open={addSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
			>
				<Alert severity="info" onClose={handleClose}>
					Success
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
